import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import api, { formatError } from "@/lib/api";

export default function ResetPin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const clean = (v) => v.replace(/\D/g, "").slice(0, 4);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pin.length !== 4) return setError("The PIN must be 4 digits.");
    if (pin !== pin2) return setError("The two PINs don't match.");
    setBusy(true);
    try {
      await api.post("/parent-pin/reset-with-token", { token, pin });
      setDone(true);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-14 text-center text-3xl tracking-[0.6em] rounded-xl border-2 border-slate-200 focus:border-blue-500 outline-none font-bold";
  return (
    <div className="min-h-screen bg-[#F8FAFC] font-parent text-slate-900 flex items-center justify-center p-5" data-testid="reset-pin-page">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-sm p-7">
        {done ? (
          <div className="text-center" data-testid="reset-pin-done">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={44} />
            <h1 className="text-2xl font-bold">New PIN saved</h1>
            <p className="text-sm text-slate-500 mt-2">You can now use it to open the parent area.</p>
            <Link to="/login" className="mt-5 inline-block rounded-full bg-blue-600 text-white font-semibold px-6 py-2.5" data-testid="reset-pin-login">Go to the app</Link>
          </div>
        ) : !token ? (
          <p className="text-center text-slate-600" data-testid="reset-pin-invalid">This reset link is not valid. Please ask for a new one from the PIN screen.</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-2"><Lock size={22} /></div>
            <h1 className="text-2xl font-bold">Choose a new parent PIN</h1>
            <input value={pin} onChange={(e) => setPin(clean(e.target.value))} inputMode="numeric" type="password" placeholder="••••" className={input} data-testid="reset-pin-input" autoFocus />
            <input value={pin2} onChange={(e) => setPin2(clean(e.target.value))} inputMode="numeric" type="password" placeholder="••••" className={input} data-testid="reset-pin-confirm" />
            <p className="text-xs text-slate-400 text-center -mt-1">Type the PIN again to confirm</p>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="reset-pin-error">{error}</p>}
            <button disabled={busy} data-testid="reset-pin-submit" className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={18} className="animate-spin" />} Save new PIN
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
